import { LitElement, html } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { styleMap } from 'lit/directives/style-map.js';
import { dispatchEvent, FocusMixin } from '@fil-global/shared';

import '@fil-global/fil-icon';
import componentStyles from './fil-button.styles.js';

/**
 * @tag fil-button
 * @property {boolean} [disabled=false] HTML disabled attribute of the button
 * @property {boolean} [expanded=false] Whether if the button width should expand to the width of its parent container or not.
 * @property {object} [extraProps] Object to pass extra props to the button
 * @property {boolean} [iconFilled=false] Whether if the icon should use its filled state if available or not
 * @property {string} [icon] Name of a GDS Streamline Icon that is shown next to the label
 * @property {"left"|"right"} [iconPosition="right"] Position of the icon
 * @property {"icon"|"primary"|"secondary"|"tertiary"|"textlink"} [kind="primary"] Type of button
 * @property {string} [label] Label that is shown on the button
 * @property {"small"|"standard"} [size="standard"] Size of button
 * @property {object} [styles] Custom style object to be applied on button
 * @property {string} [url] URL to be used with textlink variation
 * @fires click.gds.button (event: Event) => void
 */
export class FilButton extends FocusMixin(LitElement) {
  static properties = {
    disabled: { type: Boolean },
    expanded: { type: Boolean },
    extraProps: { type: Object },
    icon: { type: String },
    iconFilled: { type: Boolean },
    iconPosition: { String }, // "left" | "right"
    iconSize: { type: Number },
    kind: { type: String }, // "primary" | "secondary" | "tertiary" | "textlink" | "icon"
    label: { type: String },
    size: { type: String }, // "small" | "standard"
    styles: { type: Object },
    url: { type: String },
  };

  static styles = [componentStyles];

  constructor() {
    super();
    this.disabled = false;
    this.expanded = false;
    this.extraProps = { target: '_self' };
    this.iconFilled = false;
    this.iconPosition = 'right';
    this.kind = 'primary';
    this.size = 'standard';
    this.styles = {};
  }

  connectedCallback() {
    super.connectedCallback();
  }

  iconTemplate() {
    const iconSize =
      this.iconSize !== undefined ? this.iconSize : this.size !== 'standard' ? '16' : '20';
    const classes = {
      'fil-button__icon': true,
      [`fil-button__icon-${this.iconPosition}`]: this.label !== undefined,
    };
    return html`<span class=${classMap(classes)}>
      <fil-icon ?filled=${this.iconFilled} icon=${this.icon} size=${iconSize}></fil-icon>
    </span>`;
  }

  template() {
    return html`${this.icon ? this.iconTemplate() : ''}
    ${this.label ? html`<span class="fil-button--label">${this.label}</span>` : null}`;
  }

  handleClick() {
    dispatchEvent(this, 'click.gds.button');
  }

  render() {
    const classes = {
      [`fil-button--${this.kind}`]: this.kind,
      [`fil-button--with-icon`]: this.icon,
      [`fil-button--${this.size}`]: this.size !== 'standard',
      'fil-button--expanded': this.expanded,
    };

    return this.url && !this.disabled
      ? html`<a
          tabindex="0"
          aria-label="${this.label || 'button'}"
          ?disabled=${this.disabled}
          class="fil-button${classMap(classes)}"
          download=${ifDefined(this.extraProps.download)}
          href=${this.url}
          hreflang=${ifDefined(this.extraProps.hreflang)}
          rel=${ifDefined(this.extraProps.rel)}
          style=${styleMap(this.styles)}
          target=${ifDefined(this.extraProps.target)}
          title="${this.label}"
          type=${ifDefined(this.extraProps.type)}
          @click=${this.handleClick}
        >
          <span class="fil-button--text">${this.template()}</span>
        </a>`
      : html`<button
          tabindex="0"
          class="fil-button${classMap(classes)}"
          ?disabled=${this.disabled}
          aria-label="${this.label || 'button'}"
          title="${this.label}"
          @click=${this.handleClick}
          style=${styleMap(this.styles)}
        >
          ${this.kind === 'icon'
            ? html`<fil-icon filled icon="navigation-menu-horizontal"></fil-icon>`
            : this.template()}
        </button>`;
  }
}

export default FilButton;
